import { API_BASE_URL } from '../apiBaseUrl';

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

const waitForCurrentUser = (firebase, timeoutMs = 10000) =>
  new Promise((resolve, reject) => {
    const auth = firebase?.auth;
    if (!auth?.onAuthStateChanged) {
      reject(new Error('Firebase auth is not available.'));
      return;
    }

    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const timer = setTimeout(() => {
      unsubscribe?.();
      reject(new Error('No signed-in Firebase user found.'));
    }, timeoutMs);

    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (!user) return;
        clearTimeout(timer);
        unsubscribe();
        resolve(user);
      },
      (error) => {
        clearTimeout(timer);
        unsubscribe();
        reject(error);
      },
    );
  });

const getIdToken = async (firebase) => {
  const currentUser = firebase?.auth?.currentUser || (await waitForCurrentUser(firebase));
  return currentUser.getIdToken();
};

const parseError = async (response) => {
  try {
    return await response.json();
  } catch (_err) {
    return { error: response.statusText || 'Request failed.' };
  }
};

const apiFetch = async (firebase, path, options = {}, params) => {
  const token = await getIdToken(firebase);
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const response = await fetch(buildUrl(path, params), {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorPayload = await parseError(response);
    const error = new Error(errorPayload?.error || 'Admin Notifications API request failed.');
    error.code = errorPayload?.code || 'ADMIN_NOTIFICATIONS_API_ERROR';
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

export const adminNotificationsHealth = async (firebase) =>
  apiFetch(firebase, '/api/admin/notifications/health', { method: 'GET' });

export const adminNotificationsHistory = async (firebase, params = {}) =>
  apiFetch(firebase, '/api/admin/notifications/history', { method: 'GET' }, params);

export const adminNotificationsTemplates = async (firebase, params = {}) =>
  apiFetch(firebase, '/api/admin/notifications/templates', { method: 'GET' }, params);

export const adminNotificationsSend = async (firebase, payload, options = {}) =>
  apiFetch(
    firebase,
    '/api/admin/notifications/send',
    {
      method: 'POST',
      body: JSON.stringify(payload || {}),
      headers: options.idempotencyKey
        ? { 'X-IDEMPOTENCY-KEY': String(options.idempotencyKey) }
        : undefined,
    },
  );
